import * as React from 'react';
import { Search } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  CheckRow,
  SearchField,
  Sheet,
  SheetStat,
  WorkspaceButton,
  WorkspaceSelect,
} from '@/components/ui/workspace';
import type { KbTree } from '@/lib/api/vendor-knowledge';
import { PLATFORM_TYPES, platformLabel, plural } from '../kb-logic';

/**
 * Add products to a vendor list (Firm settings → Vendor knowledge → Vendor
 * lists): every active knowledge-base product, grouped by platform, most
 * confirmed items first, with a search and a platform filter pinned at the
 * top and only the products scrolling. Tick several and add them together;
 * products already on the list show ticked and fixed. New products go to
 * the end of the list, in the order ticked.
 */

export interface PickableProduct {
  id: string;
  name: string;
  /** The vendor's name, or null when it only repeats the product's ("Catalis"). */
  vendor: string | null;
  platform: string;
  items: number;
  onList: boolean;
}

export interface ProductGroup {
  platform: string;
  label: string;
  products: PickableProduct[];
}

/** The products a curator can add, by platform in the knowledge base's order. */
export function productGroups(
  tree: Pick<KbTree, 'vendors'>,
  onList: ReadonlySet<string>,
  query: string,
  platform: string,
): ProductGroup[] {
  const q = query.trim().toLowerCase();
  const products = tree.vendors
    .filter((v) => v.status === 'active')
    .flatMap((v) =>
      v.products
        .filter((p) => p.status === 'active')
        .map<PickableProduct>((p) => ({
          id: p.id,
          name: p.name,
          vendor: p.name.trim().toLowerCase() === v.name.trim().toLowerCase() ? null : v.name,
          platform: PLATFORM_TYPES.some((t) => t.id === p.platform_type)
            ? p.platform_type
            : 'other',
          items: p.confirmed_items,
          onList: onList.has(p.id),
        })),
    )
    .filter((p) => platform === 'all' || p.platform === platform)
    .filter((p) => q.length === 0 || `${p.vendor ?? ''} ${p.name}`.toLowerCase().includes(q));
  return PLATFORM_TYPES.map((t) => ({
    platform: t.id,
    label: t.label,
    products: products
      .filter((p) => p.platform === t.id)
      .sort((a, b) => b.items - a.items || a.name.localeCompare(b.name)),
  })).filter((g) => g.products.length > 0);
}

export function AddProductsSheet({
  tree,
  listName,
  onListIds,
  onClose,
  onAdd,
  busy,
  error,
}: {
  tree: Pick<KbTree, 'vendors'>;
  listName: string;
  onListIds: readonly string[];
  onClose: () => void;
  onAdd: (productIds: string[]) => void;
  busy: boolean;
  error: unknown;
}) {
  const [query, setQuery] = React.useState('');
  const [platform, setPlatform] = React.useState('all');
  const [ticked, setTicked] = React.useState<string[]>([]);
  const onList = React.useMemo(() => new Set(onListIds), [onListIds]);
  const groups = productGroups(tree, onList, query, platform);
  const platforms = productGroups(tree, onList, '', 'all').map((g) => g.platform);

  const toggle = (id: string) =>
    setTicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Sheet
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={`Add products to ${listName}`}
      description="Tick the knowledge-base products this list offers on engagements."
      widthClassName="w-[min(720px,94vw)]"
      summary={
        ticked.length > 0 ? (
          <SheetStat value={ticked.length.toLocaleString('en-US')} label="selected" />
        ) : (
          <span className="text-ink-500">Tick the products to add</span>
        )
      }
      actions={
        <>
          <WorkspaceButton size="md" variant="tertiary" onClick={onClose}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={ticked.length === 0 || busy}
            onClick={() => onAdd(ticked)}
          >
            {ticked.length > 0 ? `Add ${plural(ticked.length, 'product')}` : 'Add products'}
          </WorkspaceButton>
        </>
      }
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex flex-none flex-wrap items-center gap-2 border-b border-ink-100 px-5 py-4">
          <SearchField
            value={query}
            onChange={setQuery}
            aria-label="Search vendors and products"
            placeholder="Search vendors and products"
            icon={<Search className="h-3.5 w-3.5" aria-hidden="true" />}
            wrapperClassName="min-w-[220px] flex-1 gap-1.5 px-2"
          />
          <div className="w-[220px] max-w-full flex-none">
            <WorkspaceSelect
              aria-label="Platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            >
              <option value="all">All platforms</option>
              {platforms.map((id) => (
                <option key={id} value={id}>
                  {platformLabel(id)}
                </option>
              ))}
            </WorkspaceSelect>
          </div>
        </div>
        <div
          role="group"
          aria-label="Products"
          className="min-h-0 flex-1 overflow-y-auto px-5 pb-4"
        >
          {groups.length === 0 ? (
            <p className="m-0 py-6 text-center text-body-sm text-ink-500">No product matches.</p>
          ) : (
            groups.map((g) => (
              <section key={g.platform} aria-label={g.label} className="pt-3">
                <div className="sticky top-0 z-10 flex items-baseline gap-2 bg-paper-0 py-1.5">
                  <h3 className="m-0 text-meta font-semibold text-ink-700">{g.label}</h3>
                  <span className="font-mono text-[11px] text-ink-500">
                    {g.products.length.toLocaleString('en-US')}
                  </span>
                </div>
                {g.products.map((p) => (
                  <CheckRow
                    key={p.id}
                    checked={p.onList || ticked.includes(p.id)}
                    disabled={p.onList}
                    onToggle={() => toggle(p.id)}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink-900">{p.name}</span>
                      <span className="block text-meta text-ink-500">
                        {p.vendor ? `${p.vendor} · ` : ''}
                        {p.onList
                          ? 'On this list'
                          : p.items > 0
                            ? plural(p.items, 'confirmed item')
                            : 'No confirmed items yet'}
                      </span>
                    </span>
                  </CheckRow>
                ))}
              </section>
            ))
          )}
        </div>
        {error ? (
          <div className="flex-none border-t border-ink-100 px-5 py-3">
            <ErrorMessage error={error} />
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}
